import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ReceiptPercentIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { validateRecoveryPricing, validateExtrajudicialTiers } from '@claimup/shared/helpers/recovery-pricing';
import { JUDICIAL_PRICE_FIELDS, validateJudicialPricing, courtFeeField } from '@claimup/shared/helpers/judicial-pricing';
import api, { getErrorMessage } from '../../services/api';

/**
 * «Precios recobro» en el perfil de una empresa (docs/facturacion-cliente-plan.md).
 * Solo lo monta AccountPanel para el admin: la empresa ve en su Facturación lo
 * que le toca, no los precios.
 *
 * El fee es del cliente y manda sobre el % guardado en cada expediente. Las tasas
 * judiciales (suplidos) no van aquí: salen del anexo del despacho.
 */

// El judicial (docs/precios-judicial-plan.md): precio por demanda presentada y
// tasa por tipo, región e importe. Vacío = sin precio judicial: no sale en Facturación.
// También la usa la pestaña «Precios» de Facturación, para los mismos nombres.
export const JUDICIAL_INPUTS = [
  { field: 'monitorio_low', label: 'Monitorio hasta 2.000 €' },
  { field: 'monitorio_high', label: 'Monitorio de más de 2.000 €' },
  { field: 'verbal_low', label: 'Verbal hasta 2.000 €' },
  { field: 'verbal_high', label: 'Verbal de más de 2.000 €' },
  // Las tasas, por tipo, región e importe: «Tasa monitorio en España hasta 2.000 €».
  ...['monitorio', 'verbal'].flatMap(kind =>
    [
      ['rest', 'España'],
      ['cat', 'Cataluña'],
    ].flatMap(([region, lugar]) =>
      [
        ['low', 'hasta 2.000 €'],
        ['high', 'de más de 2.000 €'],
      ].map(([band, tramo]) => ({
        field: courtFeeField(kind, region, band),
        label: `Tasa ${kind} en ${lugar} ${tramo} (sin IVA)`,
      }))
    )
  ),
];

const judicialVacio = Object.fromEntries(JUDICIAL_PRICE_FIELDS.map(f => [f, '']));

const vacio = {
  extrajudicial_pct: '',
  judicial_pct: '',
  bill_judicial: true,
  monthly_cap: '',
  judicial: judicialVacio,
  // Filas del escalado: cada una guarda solo «hasta» y el %. El «desde» se deriva
  // del «hasta» anterior, así no se pueden dejar huecos. Vacío = sin escalado.
  tier_rows: [],
};

/**
 * Las filas del escalado a tramos. Los límites son INCLUSIVOS, así que cada tramo
 * empieza en el «hasta» del anterior MÁS UNO: {0,30} {31,60} {61,null}.
 * Ver docs/recobro-escalado-plan.md.
 */
export const rowsToTiers = rows =>
  rows.map((r, i) => ({
    from: i === 0 ? 0 : Number(rows[i - 1].to) + 1,
    to: r.to === '' ? null : Number(r.to),
    pct: r.pct === '' ? NaN : Number(r.pct),
  }));

const tiersToRows = tiers =>
  (tiers || []).map(t => ({ to: t.to === null || t.to === undefined ? '' : String(t.to), pct: String(t.pct) }));

const aTexto = p => ({
  extrajudicial_pct: p.extrajudicial_pct ?? '',
  judicial_pct: p.judicial_pct ?? '',
  bill_judicial: p.bill_judicial !== false,
  monthly_cap: p.monthly_cap ?? '',
  judicial: Object.fromEntries(JUDICIAL_PRICE_FIELDS.map(f => [f, p.judicial?.[f] ?? ''])),
  tier_rows: tiersToRows(p.extrajudicial_tiers),
});

/** Lo que va al servidor: vacío = null. */
export const toPricing = f => {
  const num = v => (v === '' || v === null || v === undefined ? null : Number(v));
  return {
    extrajudicial_pct: num(f.extrajudicial_pct),
    judicial_pct: num(f.judicial_pct),
    bill_judicial: Boolean(f.bill_judicial),
    monthly_cap: num(f.monthly_cap),
    extrajudicial_tiers: rowsToTiers(f.tier_rows || []),
    judicial: Object.fromEntries(JUDICIAL_PRICE_FIELDS.map(k => [k, num(f.judicial?.[k])])),
  };
};

const RecoveryPricingBlock = ({ companyId }) => {
  const [form, setForm] = useState(vacio);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/recovery-billing/companies/${companyId}/pricing`)
      .then(({ data }) => {
        if (data.data) {
          setForm(aTexto(data.data));
          setMeta(data.data);
        }
      })
      .catch(err => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [companyId]);

  const pricing = toPricing(form);
  const errors = [
    ...validateRecoveryPricing(pricing),
    ...validateExtrajudicialTiers(pricing.extrajudicial_tiers),
    ...validateJudicialPricing(pricing.judicial),
  ];
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const conEscalado = (form.tier_rows || []).length > 0;
  const setRow = (i, k, v) =>
    setForm(f => ({ ...f, tier_rows: f.tier_rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const addRow = () => setForm(f => ({ ...f, tier_rows: [...(f.tier_rows || []), { to: '', pct: '' }] }));
  const removeRow = i => setForm(f => ({ ...f, tier_rows: f.tier_rows.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/recovery-billing/companies/${companyId}/pricing`, pricing);
      setMeta(data.data);
      toast.success('Precios de recobro guardados');
    } catch (err) {
      toast.error(err?.response?.data?.error || getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-32 rounded-lg border border-slate-300 px-2 py-1';

  return (
    <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
      <div className='px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center'>
        <ReceiptPercentIcon className='h-5 w-5 text-blue-600 mr-2' />
        <h3 className='text-lg font-medium text-slate-900'>Precios recobro</h3>
        <span className='ml-2 text-xs text-slate-500'>Solo lo ve ClaimUp</span>
      </div>
      <div className='p-6 space-y-4 text-sm'>
        <p className='text-slate-600'>
          Honorarios sobre lo cobrado en el mes, sin IVA. Mandan sobre el % guardado en cada expediente. La empresa ve en
          su Facturación los honorarios que le tocan. Las tasas judiciales (suplidos) se añaden en la factura.
        </p>
        {loading ? (
          <p className='text-slate-500'>Cargando…</p>
        ) : (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              <label className='flex items-center gap-2 text-slate-700'>
                <input
                  type='number'
                  min='0'
                  max='100'
                  step='0.01'
                  value={form.extrajudicial_pct}
                  onChange={e => set('extrajudicial_pct', e.target.value)}
                  aria-label='% extrajudicial'
                  disabled={conEscalado}
                  className={`${input} ${conEscalado ? 'bg-slate-100 text-slate-400' : ''}`}
                />
                {conEscalado ? '% extrajudicial (lo manda el escalado)' : '% sobre lo cobrado en extrajudicial'}
              </label>
              <label className='flex items-center gap-2 text-slate-700'>
                <input
                  type='checkbox'
                  checked={form.bill_judicial}
                  onChange={e => set('bill_judicial', e.target.checked)}
                  aria-label='Se factura lo judicial'
                />
                Se le factura lo judicial
              </label>
              {form.bill_judicial && (
                <label className='flex items-center gap-2 text-slate-700'>
                  <input
                    type='number'
                    min='0'
                    max='100'
                    step='0.01'
                    value={form.judicial_pct}
                    onChange={e => set('judicial_pct', e.target.value)}
                    aria-label='% judicial'
                    className={input}
                  />
                  % de success fee sobre lo cobrado en judicial
                </label>
              )}
              <label className='flex items-center gap-2 text-slate-700'>
                <input
                  type='number'
                  min='0'
                  step='0.01'
                  value={form.monthly_cap}
                  onChange={e => set('monthly_cap', e.target.value)}
                  placeholder='Sin tope'
                  aria-label='Tope al mes'
                  className={input}
                />
                € de tope de honorarios al mes
              </label>
            </div>

            <div className='pt-2 border-t border-slate-100'>
              <h4 className='font-medium text-slate-800'>Escalado por días de impago</h4>
              <p className='text-xs text-slate-500 mb-2'>
                Para los clientes que no cobran un % plano sino uno por tramos, según los días que lleve impagada la
                deuda cuando entra el cobro. Sin tramos se usa el % de arriba. Los límites se incluyen: «hasta 30» son
                del día 0 al 30, y el siguiente empieza en 31.
              </p>
              {conEscalado && (
                <div className='space-y-2 mb-2'>
                  {form.tier_rows.map((r, i) => (
                    <div key={i} className='flex items-center gap-2 text-slate-700'>
                      <span className='w-24 text-right text-slate-500'>
                        {i === 0 ? 'Desde 0' : `Desde ${Number(form.tier_rows[i - 1].to) + 1 || '—'}`} días
                      </span>
                      <input
                        type='number'
                        min='0'
                        step='1'
                        value={r.to}
                        onChange={e => setRow(i, 'to', e.target.value)}
                        placeholder={i === form.tier_rows.length - 1 ? 'Sin límite' : 'hasta'}
                        aria-label={`Hasta días del tramo ${i + 1}`}
                        className='w-28 rounded-lg border border-slate-300 px-2 py-1'
                      />
                      <span className='text-slate-500'>días</span>
                      <input
                        type='number'
                        min='0'
                        max='100'
                        step='0.01'
                        value={r.pct}
                        onChange={e => setRow(i, 'pct', e.target.value)}
                        aria-label={`% del tramo ${i + 1}`}
                        className='w-24 rounded-lg border border-slate-300 px-2 py-1'
                      />
                      <span className='text-slate-500'>%</span>
                      <button
                        type='button'
                        onClick={() => removeRow(i)}
                        className='text-slate-400 hover:text-slate-700'
                        aria-label={`Quitar el tramo ${i + 1}`}
                      >
                        <TrashIcon className='h-4 w-4' />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type='button'
                onClick={addRow}
                className='inline-flex items-center gap-1 text-blue-600 hover:text-blue-700'
              >
                <PlusIcon className='h-4 w-4' />
                {conEscalado ? 'Añadir tramo' : 'Usar escalado por días'}
              </button>
            </div>

            <div className='pt-2 border-t border-slate-100'>
              <h4 className='font-medium text-slate-800'>Judicial: por demanda presentada</h4>
              <p className='text-xs text-slate-500 mb-2'>
                Se factura el mes en que se presenta la demanda, según si es monitorio o verbal y la deuda reclamada. La
                tasa va aparte y sin IVA; si el despacho trae la real, se corrige en el expediente. Vacío = sin precio.
              </p>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                {JUDICIAL_INPUTS.map(j => (
                  <label key={j.field} className='flex items-center gap-2 text-slate-700'>
                    <input
                      type='number'
                      min='0'
                      step='0.01'
                      value={form.judicial[j.field]}
                      onChange={e => setForm(f => ({ ...f, judicial: { ...f.judicial, [j.field]: e.target.value } }))}
                      aria-label={j.label}
                      className={input}
                    />
                    € · {j.label}
                  </label>
                ))}
              </div>
            </div>

            {errors.length > 0 && (
              <ul className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800 space-y-0.5'>
                {errors.map(e => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            )}

            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={save}
                disabled={saving || errors.length > 0}
                className='rounded-lg bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-700 disabled:opacity-50'
              >
                {saving ? 'Guardando…' : 'Guardar precios'}
              </button>
              <span className='text-xs text-slate-500'>
                {meta?.updated_at
                  ? `Última modificación: ${new Date(meta.updated_at).toLocaleString('es-ES')}`
                  : 'Todavía sin precios definidos.'}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RecoveryPricingBlock;
